/* ═══════════════════════════════════════════════════════════════════
   ck-device-popup.js — CaseKaro "Model Pop" v2.7.1
   One file for both flows:
     • Android  → auto-detects model, shows match popup
     • iPhone   → model picker (remembered 30 days), then match popup
     • Desktop  → does nothing, loads no data
   HAND-MAINTAINED. Do NOT regenerate this file from Apps Script.
   Model data lives in assets/ck-popup-data.json (that file IS
   auto-generated — point the Apps Script at it instead).
   Theme wiring: the existing plain script tag works unchanged — the data
   file URL is derived from this script's own src. Optionally override with
   a data-map attribute pointing at ck-popup-data.json.

   ── v2.1 (own-model suppression) ────────────────────────────────────
   Problem: the popup/pill still appeared when the visitor was already
   looking at their own model — the exact page it exists to send them to.

   1. COLLECTION MATCH IS NOW EXACT, NOT SUBSTRING.
      v2 used p.indexOf("/collections/" + m.h) !== -1. That matches on any
      path that merely contains the handle, and it silently fails the other
      way round for sibling models. We now read the first path segment after
      /collections/ and compare it whole. Tag-filtered views
      (/collections/samsung-s24-glass-cases/revolution) and collection-scoped
      product URLs still match, because the segment is unchanged.

   2. PRODUCT PAGES ARE NOW COVERED (the actual gap).
      Two checks, cheapest first:
        a. handleSlugMatch() — the model slug must sit immediately before a
           known case-type suffix at the end of the product handle. This is
           what stops the sibling bug: for a "Samsung S24" visitor,
           …-samsung-s24-back-cover matches, …-samsung-s24-ultra-back-cover
           does not, because "-ultra-" is not a case-type suffix. No network,
           no model list needed.
        b. productTagMatch() — fallback for untagged/oddly-named handles:
           fetches /products/<handle>.js and compares product.tags against
           the model name exactly. Timeboxed at 1.2s, memoised per handle,
           and defaults to "not suppressed" on any failure so a flaky
           request can never silently kill the popup site-wide.

   3. Suppression now gates the pill too — runMatchFlow() returns before
      either surface is created, so an own-model page shows nothing at all.

   ── v2.2 (iPhone picker on model pages) ────────────────────────────
   v2.1 gated the match popup but NOT the iPhone picker, because "we don't
   know their model yet". That was wrong: on a model page the URL already
   answers the question. An iPhone visitor with no remembered model was
   still getting the picker thrown over /collections/iphone-15-glass-cases
   (and its /revolution view), which is the page the picker exists to reach.

   pageIdentifiesAModel() now resolves the model straight from the URL:
     • collection → exact handle lookup against every h/gh in the data file
     • product    → strip the case-type suffix, then test each tail of the
                    remaining handle against the model-slug set, longest
                    first. Same sibling-safe rule as handleSlugMatch.
   Both indexes are built once per page load and cached, so this is a couple
   of Set lookups, not a scan of 1,586 entries.

   The picker AND its skipped-state pill are now behind that check. We do
   not persist the inferred model — someone browsing an iPhone 15 page may
   be buying a gift, and a wrong remembered model is worse than none.

   ── v2.3 (Smash the Grid + real event tracking) ─────────────────────
   1. TRACKING NOW ACTUALLY GOES SOMEWHERE. track() fans out to whatever
      analytics the page actually has — the Meta pixel (fbq trackCustom),
      gtag, and dataLayer — each guarded independently so one missing library
      cannot break the others.

      CORRECTION (v2.4.2): v2.3 also fanned out to window.ckTrack, on the
      belief that nothing defined it. That was wrong — assets/ck-android-
      detector.js defines it, and it is a narrow Apps Script collector that
      recognises exactly two event names and discards the rest to a Debug
      sheet. Firing mp_pill_shown into it meant a retrying POST to Apps Script
      on nearly every mobile session. This file now uses its own hook,
      window.ckPopupTrack, and never touches window.ckTrack.

      New events: mp_pill_shown / mp_pill_cta (the pill's own reach and CTR,
      never measured before — this is the number the game has to beat),
      mp_game_tap / mp_game_start / mp_game_end / mp_game_cta /
      mp_game_quit / mp_game_unavailable.

   2. THE PILL CAN START A GAME. A ▶ button on the minimised pill morphs the
      pill itself into a breakout paddle; the bricks are that model's eight
      best-selling cases, and the end sheet is the cases they smashed with a
      CTA into the collection. The game lives in a SEPARATE asset,
      ck-pill-game.js, injected on the first play tap and derived from this
      script's own src — so it needs no theme.liquid edit, and visitors who
      never play never download it.

      Products are fetched at limit=8 only on that tap. The sheet keeps its
      limit=3 fetch; nobody who isn't playing pays for the bigger payload.
      Collections are sortOrder BEST_SELLING, so limit=8 is already "the
      eight best sellers for this exact model" with no extra work.

   3. KILL SWITCH. Set window.CK_GAME_OFF = true (or flip CFG.gameOn below)
      and the ▶ never renders — the pill reverts to exactly its v2.2
      behaviour. Use this rather than reverting the file if the game needs
      to come down in a hurry.

      To tune the game without touching either asset, set
      window.CK_GAME_CFG = { speed: 7, lives: 2, ... } in theme.liquid.

   ── v2.4 (field fixes + two-board pill) ─────────────────────────────
   1. THE GAME WAS UNREACHABLE ON IPHONE. v2.3 only rendered the play
      arrow when it already knew the model. On iOS it never does until the
      visitor picks one, so anybody who dismissed the picker got a pill
      with no arrow — reported as "the game doesn't work on iOS at all".
      The arrow is now always present; with no model it opens the picker
      and drops straight into the game once they choose.

   2. THE PILL IS TWO BOARDS. A coloured board sits behind the white one,
      longer on the left, and the exposed end carries the play arrow. In
      the game that same coloured board becomes the paddle base and the
      white one becomes the model-name plate, so the object the visitor
      taps is the object they then play with.

   3. The paddle moved into the canvas (see ck-pill-game.js v2.0). v2.3
      kept it as this DOM pill while the ball lived in a canvas sized to
      innerHeight — two coordinate systems that disagree by the height of
      a mobile URL bar. That is the "board at the bottom was missing" bug,
      and why it came right on a second play once the bar had settled.

   ── v2.6 (this design for your phone) ───────────────────────────────
   On a product page made for ANOTHER phone — usually a Meta catalogue card
   that showed whatever model it chose — this popup opened with three top
   sellers for the visitor's phone, all linking to the collection. The design
   they tapped in the ad was not among them unless by chance.

   The first tile is now that design for their phone ("This design"), linking
   straight to its product page; two top sellers follow. The lookup belongs to
   assets/Ck-switch-model.js (v2.0+), which already resolves "same design,
   other phone" for its own button: this file only asks
   window.__ckSwitch.mineFor(entry). Off product pages, on non-design
   products, or if the switcher is missing or slow, the popup is exactly
   v2.5.2 — it waits at most CFG.thisDesignWait for the answer and slots the
   tile in if it arrives while the sheet is still open.
   Events: mp_this_design_shown {model, late}, mp_this_design {model}.
   The tap also leaves sessionStorage ck_sw_used = "pop", which the switcher
   turns into cart attribute ck_switch on the next page.

   ── v2.7 (only where it belongs) ─────────────────────────────────────
   Audit on the live store, 9 Oct, as a Samsung A17 visitor, an iPhone with
   no saved model and an iPhone 15 owner. The popup (or the iPhone picker)
   opened on order tracking, returns, contact, FAQ, about, reviews, the
   bio-link page, policies, blog posts, search, accessory products and the
   metal model picker; and on the visitor's OWN phone's pages whenever the
   handle wasn't the standard one — price bands (samsung-a17-5g-cases-
   under-199), anime and metal collections, products with a design code
   after the suffix (white-dots-samsung-a17-5g-back-cover-0530). A Vivo V60e
   owner (whole range sold out since 7 Oct) got three sold-out top sellers.

   1. PAGE GATE, pageAllows(): home, collections, products and /pages/
      pages named after cases or phones only; never 404s; on product pages
      only phone cases (TYPE-BATCH-MODEL-DESIGN SKU). Covers the popup, the
      pill and the iPhone picker.
   2. THEIR OWN PHONE, any handle: the model index now maps every model
      slug (also taken from older collection handles) to the model, and the
      longest slug in a handle names the page's phone. Design products are
      also checked by SKU through Ck-switch-model.js (isFor, v2.1+).
   3. IN STOCK ONLY: tiles skip sold-out products; a phone with nothing in
      stock gets no popup and no pill (mp_suppressed, why: out_of_stock),
      and an empty sheet never opens by itself (why: no_products).
   The checks run side by side and the 800 ms delay counts the time they
   took, so the popup opens no later than before on ordinary pages.
   Events: mp_suppressed {why: page | out_of_stock}; own-model and
   after-a-switch silences keep mp_suppressed_own_model.

   ── v2.7.1 (one download each) ──────────────────────────────────────
   The model file and this product's .js go through window.__ckGet: one
   fetch per URL per page, shared with Ck-switch-model.js (v2.2+), which
   now reads the same versioned data-map URL. Before, the switcher fetched
   its own unversioned copy of the model file (kept for a year by
   browsers) and both files fetched the product .js.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  /* ── Config ──────────────────────────────────────────────────── */
  var CFG = {
    delayMatch:   800,     // ms before match popup appears
    delayPicker:  2500,    // ms before iPhone picker appears (first visit)
    fetchTimeout: 4000,    // ms budget for products fetch
    productTimeout: 2500,  // ms budget for the product lookup (is it a case? whose tags?)
    rememberDays: 30,      // how long a picked iPhone model is remembered
    pillBottom:   16,      // px — raise if it overlaps chat/checkout widgets
    zIndex:       99999,
    gameOn:       true,    // master switch for the pill game
    gameProducts: 8,       // bricks — collections are BEST_SELLING sorted
    gameMinProducts: 2,    // below this we don't bother offering a game
    gameTimeout:  6000,    // ms budget for loading the game + its products
    gameAccent:   "#111318", // the board behind the pill; also the paddle base
    gameCoupon:   null,    // {code,label,value,terms} — see showEndSheet
    gameCartTag:  true,    // stamp /cart/update.js so plays land on the ORDER
    gameLogUrl:   null,    // optional Apps Script web-app URL for raw play rows
    gamePeek:     40,      // px of that board left visible beside the pill
    gameOverlap:  16,      // px of it hidden under the white board
    thisDesignWait: 1500   // ms the match popup waits for "this design for your phone"
  };
  try { if (window.CK_POPUP_CFG) for (var _pk in window.CK_POPUP_CFG) CFG[_pk] = window.CK_POPUP_CFG[_pk]; } catch (e) {}

  var _playAfterPick = false;   // picker was opened by the play arrow

  var MOCK = window.__CK_MOCK || null;   // used only by the design preview
  if (MOCK && MOCK.fastDelays) { CFG.delayMatch = 300; CFG.delayPicker = 500; }

  /* Where the model data lives. Captured at parse time (currentScript is null
     later, inside callbacks). Prefers an explicit data-map attribute; otherwise
     derives the sibling asset URL from this script's own src, so the theme needs
     no extra markup — the existing plain script tag keeps working. */
  var SELF = document.currentScript || document.querySelector('script[src*="ck-device-popup"]');
  var SELF_SRC = (SELF && SELF.src) ? SELF.src.split("?")[0] : null;
  /* The popup's own ?v= — Shopify regenerates it on every upload of THIS
     file, so carrying it across is a free cache-buster for its siblings. */
  var SELF_Q = (SELF && SELF.src && SELF.src.indexOf("?") !== -1)
                 ? SELF.src.slice(SELF.src.indexOf("?")) : "";
  var ASSET_V = "2.7.1";     // bump when shipping if theme.liquid ever drops ?v=

  var DATA_URL = (SELF && SELF.getAttribute("data-map")) ||
                 (SELF_SRC ? SELF_SRC.replace(/ck-device-popup\.js$/, "ck-popup-data.json") : null);

  /* Same derivation for the game. It is NOT in theme.liquid on purpose.

     CACHE-BUSTING IS NOT OPTIONAL HERE. Because this URL is built by hand
     rather than by Shopify's asset_url filter, it carries no version query
     of its own. Ship a new ck-pill-game.js and the URL is byte-identical to
     the last one, so the CDN and every returning visitor's browser keep
     serving the OLD game against the NEW popup — which presents as "I
     deployed it and nothing changed". Append the popup's own ?v=, falling
     back to ASSET_V above. */
  var GAME_URL = (SELF && SELF.getAttribute("data-game")) ||
                 (SELF_SRC
                   ? SELF_SRC.replace(/ck-device-popup\.js$/, "ck-pill-game.js") +
                     (SELF_Q || "?v=" + ASSET_V)
                   : null);

  /* ── Session/storage keys (compatible with v1 where possible) ── */
  var K = {
    seen:   "ck_popup_seen",      // match popup shown this session
    match:  "ck_match",           // cached Android match entry
    iModel: "ck_iphone_model_v2", // remembered iPhone model (localStorage)
    iSkip:  "ck_ip_skip"          // picker skipped this session
  };

  /* ── Suppression rules ───────────────────────────────────────── */
  /* Pages that exist to pick a phone, or that a popup would interrupt.
     v2.7 added the metal picker and the two finder landing pages. */
  var SUPPRESSED_PATHS = [
    "/cart", "/checkout", "/checkouts", "/orders",
    "/collections/glass-cases-by-design",
    "/pages/phone-cases-by-model",
    "/pages/glass-phone-cases-by-model",
    "/pages/metal-phone-cases-by-model",
    "/pages/find-your-phone-case",
    "/pages/find-your-glass-case",
    "/pages/what-is-my-phone",
    "/pages/predict-and-win",
    "/account"
  ];
  var SUPPRESSED_SUFFIXES = ["-glass-cases", "-back-covers", "-metal-cases"];

  /* v2.7 — a /pages/ page is a shopping page only if its handle has one of
     these words (samsung-back-covers-cases, glass-case, mobile-covers …).
     Order tracking, returns, contact, FAQ, about, reviews and the bio-link
     page have none. Whole words: "casekaro-tracking" must not match "case". */
  var SHOP_WORDS = { "cover": 1, "covers": 1, "case": 1, "cases": 1, "phone": 1, "phones": 1, "mobile": 1 };
  /* Phone-case SKUs are TYPE-BATCH-MODEL-DESIGN. A product with none
     (cable protector, sticky pod, mousepad, stickers) is not a phone case. */
  var CASE_SKU = /^(CK|GC|TP|BS|MC)-\d{3,4}-[A-Za-z0-9]+-\d{3,4}$/i;

  /* Case-type endings a product handle can carry. Used as the right-hand
     boundary when matching a model slug inside a product handle — this is
     what makes "samsung-s24" reject "…-samsung-s24-ultra-back-cover".
     Add new product lines here when they launch. */
  var PRODUCT_SUFFIXES = [
    "back-cover", "glass-case", "metal-case", "pop-case",
    "soft-case", "silicone-case", "pop-socket", "magsafe-case"
  ];

  /* ── Platform ────────────────────────────────────────────────── */
  var UA = navigator.userAgent;
  var isIphone  = MOCK ? MOCK.platform === "iphone"  : /iPhone/i.test(UA);
  var isAndroid = MOCK ? MOCK.platform === "android" : /Android/i.test(UA);
  if (!isIphone && !isAndroid) return;   // desktop: nothing, no data load

  /* ══════════════════════════════════════════════════════════════
     Tracking

     Fan out to every analytics library that actually exists on the page,
     each in its own try/catch so a missing or throwing library cannot take
     the others (or the popup) down with it. fbq is the one most likely to
     be present given the Meta pixel; custom events land in Events Manager
     and can be used for audiences.
     ══════════════════════════════════════════════════════════════ */
  /* ⚠ DO NOT CALL window.ckTrack FROM HERE.

     assets/ck-android-detector.js defines window.ckTrack, and it is NOT a
     general-purpose sink: it POSTs to a Google Apps Script endpoint, takes
     only an event NAME (params are dropped), and its backend recognises
     exactly two strings — no_match_shown and no_match_clicked. Everything
     else is written to a Debug sheet and discarded.

     v2.4.1 fanned out to it, which meant mp_pill_shown — an event that fires
     on nearly every mobile session — became a retrying POST to Apps Script on
     nearly every mobile session. That is a quota fire, not analytics.

     This script uses its own hook instead. Define window.ckPopupTrack if you
     want a dedicated collector; ck-android-detector.js is left alone. */
  function track(ev, params) {
    var p = params || {};
    try { if (typeof window.ckPopupTrack === "function") window.ckPopupTrack(ev, p); } catch (e) {}
    try { if (typeof window.fbq === "function") window.fbq("trackCustom", ev, p); } catch (e) {}
    try {
      if (typeof window.gtag === "function") {
        /* transport_type beacon → sendBeacon, which survives the unload that
           immediately follows CTA clicks. Without it the single most important
           event in this funnel (game → collection) is the one most likely to
           be dropped. */
        var g = { transport_type: "beacon" };
        for (var gk in p) { if (Object.prototype.hasOwnProperty.call(p, gk)) g[gk] = p[gk]; }
        window.gtag("event", ev, g);
      }
    } catch (e) {}
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        var o = { event: ev };
        for (var k in p) { if (Object.prototype.hasOwnProperty.call(p, k)) o[k] = p[k]; }
        window.dataLayer.push(o);
      }
    } catch (e) {}
  }

  /* ── Small helpers ───────────────────────────────────────────── */
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function money(v) { var n = parseFloat(v); return isNaN(n) ? "" : "₹" + Math.round(n); }
  function slugify(s) {
    return String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }
  function rxEsc(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
  /* Shopify CDN resize. Works for both /products/x.jpg and /files/x.avif?v=…
     Anything that isn't an http(s) URL is passed through untouched — appending
     a query string to a data: URI corrupts the base64 payload and every image
     silently fails to decode. */
  function thumb(u, w) {
    if (!u) return "";
    if (!/^https?:\/\//i.test(u) && u.indexOf("//") !== 0) return u;
    return u + (u.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  /* First path segment after /collections/ — null when not a collection URL.
     /collections/samsung-s24-glass-cases/revolution → "samsung-s24-glass-cases"
     /collections/samsung-s24-back-covers/products/x → "samsung-s24-back-covers" */
  function collectionHandle() {
    var m = location.pathname.toLowerCase().match(/^\/collections\/([^\/?#]+)/);
    return m ? m[1] : null;
  }
  /* Product handle from either /products/x or /collections/y/products/x */
  function productHandle() {
    var m = location.pathname.toLowerCase().match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function isPathSuppressed() {
    if (MOCK) return false;
    var p = location.pathname.toLowerCase();
    for (var i = 0; i < SUPPRESSED_PATHS.length; i++) {
      if (p === SUPPRESSED_PATHS[i] || p.indexOf(SUPPRESSED_PATHS[i] + "/") === 0) return true;
    }
    return false;
  }

  /* v2.7 — only shopping pages get the popup, the pill or the iPhone picker:
     home, collections, products, and /pages/ pages named after cases or
     phones. Not search, blogs, policies, account or utility pages. */
  function isShoppingPath() {
    if (MOCK) return true;
    var p = location.pathname.toLowerCase();
    if (p === "/" || /^\/collections(\/|$)/.test(p) || /\/products\//.test(p)) return true;
    var m = p.match(/^\/pages\/([^\/?#]+)/);
    if (!m) return false;
    var w = m[1].split("-");
    for (var i = 0; i < w.length; i++) if (SHOP_WORDS[w[i]]) return true;
    return false;
  }

  /* This product's own data, fetched once per page and shared (is it a case?
     whose tags?). Timeboxed; null when slow or failed, which every caller
     treats as "don't know" and fails open. */
  var _prodP = null;
  function productData() {
    if (_prodP) return _prodP;
    var h = productHandle();
    _prodP = (h && !MOCK)
      ? withTimeout(ckGet("/products/" + h + ".js"), CFG.productTimeout, null)
      : Promise.resolve(null);
    return _prodP;
  }

  /* v2.7.1 — one fetch per URL per page, shared with Ck-switch-model.js
     (same helper there; the URL strings must match). Parsed JSON or null;
     never rejects; a failed URL may be fetched again later. */
  function ckGet(u) {
    var c = window.__ckGet || (window.__ckGet = {});
    if (!c[u]) c[u] = fetch(u, { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (j) { if (j == null) delete c[u]; return j; });
    return c[u];
  }
  function isCaseProduct(d) {
    var vs = (d && d.variants) || [];
    for (var i = 0; i < vs.length; i++) if (CASE_SKU.test(String(vs[i].sku || ""))) return true;
    return false;
  }

  /* One page gate for every surface (match popup, pill, iPhone picker).
     Resolves true when this page may show one. */
  function pageAllows() {
    if (MOCK) return Promise.resolve(true);
    if (isPathSuppressed() || !isShoppingPath()) return Promise.resolve(false);
    if (document.body && /(^|\s)template-404(\s|$)/.test(document.body.className)) return Promise.resolve(false);
    if (!productHandle()) return Promise.resolve(true);
    return productData().then(function (d) { return d ? isCaseProduct(d) : true; });
  }

  /* ── "Which phone is this page about?" ──────────────────────────
     One index, built once per page from the data file: exact collection
     handles, and every model's slug → that model. v2.7 also takes slugs from
     the collection handles with the case-type ending cut off, because older
     collections are named differently ("samsung-galaxy-s22-back-covers-cases"
     for "Samsung S22", "iphone-12-mini-cover-case"). */
  var _idx = null;
  var HANDLE_TAIL = /-(back-covers?|glass-cases?|metal-cases?|covers?|cases?)(-(cases?|covers?))?$/;
  function buildIndex(data) {
    if (_idx) return _idx;
    var handles = {}, slugs = {};
    var maps = [];
    if (data && data.android) maps.push(data.android);
    if (data && data.iphone && data.iphone.map) maps.push(data.iphone.map);
    function add(s, nm) { if (s && !slugs[s]) slugs[s] = nm; }
    for (var i = 0; i < maps.length; i++) {
      for (var k in maps[i]) {
        var e = maps[i][k];
        if (!e) continue;
        var nm = slugify(e.n || k);             // iphone map entries carry no .n
        if (e.h)  { handles[String(e.h).toLowerCase()]  = 1; add(String(e.h).toLowerCase().replace(HANDLE_TAIL, ""), nm); }
        if (e.gh) { handles[String(e.gh).toLowerCase()] = 1; add(String(e.gh).toLowerCase().replace(HANDLE_TAIL, ""), nm); }
        add(nm, nm);
      }
    }
    _idx = { handles: handles, slugs: slugs };
    return _idx;
  }

  /* Longest model slug inside a handle, on whole words; a tie goes to the
     one nearer the end (product handles are design, then model). Sibling-
     safe: "iphone-15-pro-anime-case" is the iPhone 15 Pro, not the iPhone 15. */
  function slugInHandle(handle, slugs) {
    var w = String(handle || "").split("-"), best = null, bl = 0;
    for (var i = 0; i < w.length; i++) {
      for (var j = w.length; j > i && j - i >= bl; j--) {
        var s = w.slice(i, j).join("-");
        if (slugs[s]) { best = s; bl = j - i; break; }
      }
    }
    return best;
  }
  /* The slugs that mean this phone: its name, and its collection handles
     without the case-type ending (covers index collisions between models). */
  function phoneSlugs(m) {
    var o = {}, n = slugify(m.n);
    if (n) o[n] = 1;
    if (m.h)  o[String(m.h).toLowerCase().replace(HANDLE_TAIL, "")] = 1;
    if (m.gh) o[String(m.gh).toLowerCase().replace(HANDLE_TAIL, "")] = 1;
    return o;
  }
  function namesThisPhone(handle, m) {
    if (!_idx || !handle) return false;
    var s = slugInHandle(handle, _idx.slugs);
    return !!s && (!!phoneSlugs(m)[s] || _idx.slugs[s] === slugify(m.n));
  }

  /* Already on this model's collection page? Exact handle as before; v2.7 also
     any collection whose handle names this model — price bands
     ("…-cases-under-199"), anime, metal, MagSafe, older handle styles. */
  function isModelSuppressed(m) {
    if (MOCK) return false;
    var ch = collectionHandle();
    if (!ch) return false;
    if (m.h  && ch === String(m.h).toLowerCase())  return true;
    if (m.gh && ch === String(m.gh).toLowerCase()) return true;
    var slug = slugify(m.n);
    if (!slug) return false;
    for (var j = 0; j < SUPPRESSED_SUFFIXES.length; j++) {
      if (ch === slug + SUPPRESSED_SUFFIXES[j]) return true;
    }
    return namesThisPhone(ch, m);
  }

  /* Already on a product page for this model? Slug followed by a case-type
     suffix at the end of the handle, so sibling models miss; v2.7 also allows
     a design code after it ("white-dots-samsung-a17-5g-back-cover-0530") and
     any handle that names this model ("…-clear-case-for-iphone-17"). */
  function handleSlugMatch(m) {
    if (MOCK) return false;
    var h = productHandle();
    if (!h) return false;
    var slug = slugify(m.n);
    if (!slug) return false;
    var re = new RegExp("(?:^|-)" + rxEsc(slug) + "-(?:" + PRODUCT_SUFFIXES.join("|") + ")s?(?:-\\d+)?$");
    return re.test(h) || namesThisPhone(h, m);
  }

  /* Fallback for handles that don't follow the naming convention: ask the
     product itself. Fails open (false). */
  function productTagMatch(m) {
    if (MOCK || !productHandle()) return Promise.resolve(false);
    var want = String(m.n || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!want) return Promise.resolve(false);
    return productData().then(function (d) {
      var tags = (d && d.tags) || [];
      for (var i = 0; i < tags.length; i++) {
        if (String(tags[i]).toLowerCase().replace(/\s+/g, " ").trim() === want) return true;
      }
      return false;
    });
  }

  /* v2.7 — design products: same phone by SKU. Ck-switch-model.js (v2.1+)
     knows this page's SKU and each model's SKU code. Fails open (false). */
  function productSkuMatch(m) {
    try {
      var sw = window.__ckSwitch;
      if (!MOCK && productHandle() && sw && typeof sw.isFor === "function") {
        return withTimeout(Promise.resolve(sw.isFor(m)), 2500, null)
          .then(function (v) { return v === true; }, function () { return false; });
      }
    } catch (e) {}
    return Promise.resolve(false);
  }

  /* Longest-tail match: "spider-man-samsung-s24" → tries the whole string,
     then "man-samsung-s24", then "samsung-s24" … first hit is the longest
     real model, so S24 Ultra can never be read as S24. */
  function modelSlugInHandle(handle, slugs) {
    var m = handle.match(new RegExp("^(.*)-(?:" + PRODUCT_SUFFIXES.join("|") + ")s?$"));
    var stem = m ? m[1] : handle;
    var parts = stem.split("-");
    for (var i = 0; i < parts.length; i++) {
      var tail = parts.slice(i).join("-");
      if (slugs[tail]) return tail;
    }
    return null;
  }

  function pageIdentifiesAModel(data) {
    if (MOCK) return false;
    var idx = buildIndex(data);
    var ch = collectionHandle();
    if (ch && (idx.handles[ch] || slugInHandle(ch, idx.slugs))) return true;   // v2.7: any handle naming a model
    var ph = productHandle();
    if (ph && (modelSlugInHandle(ph, idx.slugs) || slugInHandle(ph, idx.slugs))) return true;
    return false;
  }

  /* Single gate used by runMatchFlow. Resolves false to go ahead, or the
     reason to stay silent: "switched", "page" (not a shopping page, or not a
     phone case), "own" (this page is already for their phone) or
     "out_of_stock" (nothing to sell them). The stock check and the glass
     check start as soon as the page passes, alongside the own-phone checks;
     the popup reuses both fetches. */
  function shouldStaySilent(entry) {
    try { if (sessionStorage.getItem("ck_switched")) return Promise.resolve("switched"); } catch (e) {}
    if (MOCK) return Promise.resolve(false);
    return pageAllows().then(function (ok) {
      if (!ok) return "page";
      var stock = modelInStock(entry);
      glassAvailable(entry);
      if (isModelSuppressed(entry)) return "own";
      /* A product handle that names a phone answers the question by itself;
         only odd handles need the product's tags or its SKU. */
      var ph = productHandle(), named = ph && _idx ? slugInHandle(ph, _idx.slugs) : null;
      var own = (!ph || named) ? Promise.resolve(handleSlugMatch(entry))
        : Promise.all([productTagMatch(entry), productSkuMatch(entry)]).then(function (r) { return r[0] || r[1]; });
      return own.then(function (o) {
        if (o) return "own";
        return stock.then(function (s) { return s ? false : "out_of_stock"; });
      });
    });
  }

  /* ── Data loading (only on mobile, cached per session) ───────── */
  var _dataPromise = null;
  function loadData() {
    if (_dataPromise) return _dataPromise;
    if (MOCK && MOCK.data) { _dataPromise = Promise.resolve(MOCK.data); return _dataPromise; }
    if (!DATA_URL) { _dataPromise = Promise.reject(new Error("ck: data url unresolved")); return _dataPromise; }
    _dataPromise = ckGet(DATA_URL).then(function (d) { if (!d) throw new Error("ck: data unavailable"); return d; });
    return _dataPromise;
  }

  /* ── Styles ──────────────────────────────────────────────────── */
  function injectCss() {
    if (document.getElementById("ckp-style")) return;
    var s = document.createElement("style");
    s.id = "ckp-style";
    s.textContent = [
      ':root{--ckp-ink:#111;--ckp-mut:#6b6b6b;--ckp-line:#ececec;--ckp-bg:#fff;--ckp-z:' + CFG.zIndex + ';}',
      '#ckp-ov{position:fixed;inset:0;background:rgba(17,17,17,.55);z-index:var(--ckp-z);display:flex;align-items:flex-end;justify-content:center;opacity:0;transition:opacity .25s ease;}',
      '#ckp-ov.on{opacity:1;}',
      '@media(min-width:750px){#ckp-ov{align-items:center;padding:24px;}}',
      '.ckp-sheet{background:var(--ckp-bg);width:100%;max-width:420px;border-radius:20px 20px 0 0;box-shadow:0 -8px 40px rgba(0,0,0,.18);position:relative;transform:translateY(24px);transition:transform .28s cubic-bezier(.22,.9,.3,1);max-height:88vh;display:flex;flex-direction:column;overflow:hidden;}',
      '@media(min-width:750px){.ckp-sheet{border-radius:20px;transform:translateY(12px) scale(.98);}}',
      '#ckp-ov.on .ckp-sheet{transform:translateY(0) scale(1);}',
      '.ckp-sheet *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;margin:0;}',
      '.ckp-grab{width:36px;height:4px;border-radius:2px;background:#e3e3e3;margin:10px auto 0;flex:none;}',
      '@media(min-width:750px){.ckp-grab{display:none;}}',
      /* Close control.

         TWO separate problems lived here. The icon was a U+2715 text glyph, so
         its weight and centring changed with whatever font the device picked.
         And the button was never actually a circle on the live store: the Xtra
         theme styles buttons with #root-prefixed selectors, e.g. `#root button`,
         which scores higher than a bare `.ckp-x` class — so the theme's padding
         and min-width won and stretched a 32x32 circle into a wide ellipse.
         Swapping the glyph changed nothing visible because the SHAPE was the
         thing that looked wrong, and the shape was wrong in both versions.

         This widget is injected into someone else's stylesheet, so geometry is
         now stated at ID+class specificity AND marked important. Belt and
         braces is correct here — a host theme can and does out-specify us. */
      '#ckp-ov .ckp-x{position:absolute!important;top:10px!important;right:10px!important;' +
        'width:32px!important;height:32px!important;min-width:0!important;max-width:none!important;' +
        'min-height:0!important;max-height:none!important;padding:0!important;margin:0!important;' +
        'border:none!important;border-radius:50%!important;background:#f1f1f3!important;' +
        'color:#3c3c41!important;display:flex!important;align-items:center!important;' +
        'justify-content:center!important;box-shadow:none!important;line-height:1!important;' +
        'font-size:0!important;letter-spacing:normal!important;flex:none!important;' +
        'text-transform:none!important;z-index:2;cursor:pointer;-webkit-appearance:none;appearance:none;' +
        '-webkit-tap-highlight-color:transparent;}',
      '#ckp-ov .ckp-x svg{width:14px!important;height:14px!important;display:block!important;flex:none!important;}',
      '#ckp-ov .ckp-x:active{background:#e4e4e7!important;}',
      /* The sheet focuses this button on open so keyboard and screen-reader
         users land somewhere sensible. That also painted the browser's default
         blue focus ring over a branded sheet on every touch open, which is a
         large part of why it looked foreign. :focus-visible keeps the ring for
         keyboard users and drops it for taps. */
      '.ckp-sheet:focus{outline:none;}',
      '#ckp-ov .ckp-x:focus{outline:none!important;}',
      '#ckp-ov .ckp-x:focus-visible{outline:2px solid #111!important;outline-offset:2px!important;}',
      '#ckp-ov .ckp-x:hover{background:#e8e8eb!important;}',
      '.ckp-head{padding:18px 20px 4px;flex:none;}',
      '.ckp-eyebrow{font-size:10px;font-weight:700;letter-spacing:.12em;color:var(--ckp-mut);text-transform:uppercase;margin-bottom:6px;}',
      '.ckp-eyebrow.win{color:#1a8f4a;}',
      '.ckp-eyebrow.lose{color:#b0562a;}',
      '.ckp-title{font-size:21px;font-weight:800;color:var(--ckp-ink);line-height:1.2;letter-spacing:-.01em;padding-right:36px;}',
      '.ckp-sub{font-size:13px;color:var(--ckp-mut);margin-top:5px;}',
      '.ckp-row{display:flex;justify-content:space-between;align-items:baseline;margin:0 0 10px;}',
      '.ckp-rowt{font-size:13.5px;font-weight:700;color:var(--ckp-ink);}',
      '.ckp-seeall{font-size:12px;color:var(--ckp-mut);text-decoration:none;font-weight:600;white-space:nowrap;}',
      '.ckp-seeall:hover{color:var(--ckp-ink);}',
      '.ckp-proof{font-size:11.5px;color:#8a8a8a;text-align:center;margin:10px 0 0;}',
      '.ckp-proof b{color:var(--ckp-ink);font-weight:700;}',
      '.ckp-body{padding:14px 20px 0;overflow-y:auto;}',
      '.ckp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}',
      '.ckp-card{display:block;text-decoration:none;border:1px solid var(--ckp-line);border-radius:12px;overflow:hidden;background:#fff;position:relative;}',
      '.ckp-card:active{transform:scale(.98);}',
      '.ckp-img{width:100%;aspect-ratio:5/8;background:#fff;display:flex;align-items:center;justify-content:center;overflow:hidden;}',
      '.ckp-img img{width:100%;height:100%;object-fit:contain;display:block;}',
      '.ckp-tag{position:absolute;top:6px;left:6px;background:#111;color:#fff;font-size:8.5px;font-weight:700;letter-spacing:.05em;padding:3px 6px;border-radius:4px;text-transform:uppercase;}',
      '.ckp-meta{padding:7px 8px 9px;}',
      '.ckp-name{font-size:10.5px;color:var(--ckp-mut);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:3px;}',
      '.ckp-price{font-size:13px;font-weight:700;color:var(--ckp-ink);display:flex;gap:5px;align-items:baseline;}',
      '.ckp-cmp{font-size:10.5px;font-weight:400;color:#9a9a9a;text-decoration:line-through;}',
      '.ckp-ctas{padding:14px 20px 10px;flex:none;}',
      '.ckp-btn{display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:14px;border-radius:12px;background:var(--ckp-ink);color:#fff;font-size:14px;font-weight:700;text-decoration:none;border:none;cursor:pointer;}',
      '.ckp-btn:hover{background:#2b2b2b;}',
      '.ckp-btn2{display:flex;align-items:center;justify-content:center;width:100%;padding:12px;border-radius:12px;background:#fff;color:var(--ckp-ink);font-size:13px;font-weight:600;text-decoration:none;border:1px solid #d9d9d9;margin-top:8px;cursor:pointer;}',
      '.ckp-btn2:hover{border-color:#111;}',
      '.ckp-foot{text-align:center;padding:2px 20px 16px;flex:none;}',
      '.ckp-foot a{font-size:12px;color:var(--ckp-mut);text-decoration:none;cursor:pointer;}',
      '.ckp-foot a span{color:var(--ckp-ink);font-weight:600;text-decoration:underline;text-underline-offset:2px;}',
      /* reward block — only rendered when CFG.gameCoupon is set */
      '.ckp-rew{margin:14px 0 2px;border:1.5px dashed #d3d7dd;border-radius:12px;padding:12px 14px;text-align:center;background:#fcfcfd;}',
      '.ckp-rewt{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#1a8f4a;margin-bottom:6px;}',
      '.ckp-code{font-size:20px;font-weight:900;letter-spacing:.14em;color:var(--ckp-ink);font-family:ui-monospace,Menlo,monospace;}',
      '.ckp-code small{display:block;font-family:inherit;font-size:10.5px;font-weight:600;letter-spacing:.02em;color:#9a9a9a;margin-top:5px;}',
      '.ckp-rewh{font-size:11px;color:#8a8a8a;margin-top:7px;}',
      /* picker */
      '.ckp-plist{padding:6px 20px 12px;overflow-y:auto;}',
      '.ckp-series{font-size:10.5px;font-weight:700;letter-spacing:.1em;color:#9a9a9a;text-transform:uppercase;margin:12px 0 7px;}',
      '.ckp-series:first-child{margin-top:2px;}',
      '.ckp-chips{display:grid;grid-template-columns:1fr 1fr;gap:7px;}',
      '.ckp-chip{padding:12px 10px;border:1px solid #e0e0e0;border-radius:10px;background:#fff;font-size:13px;font-weight:600;color:var(--ckp-ink);cursor:pointer;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.ckp-chip:hover{border-color:#111;}',
      '.ckp-chip:active{background:#f4f4f4;}',
      /* pill — v2.4 is two stacked boards. The white one is the pill you
         already know. Behind it sits a coloured board, longer on the left,
         and the only part of it you can see is the end carrying the play
         arrow. Reads as "there is another board back there", and it is
         literally true: in the game that coloured board becomes the base
         of the paddle and the white one becomes the model-name plate. */
      '#ckp-pill{position:fixed;bottom:' + CFG.pillBottom + 'px;right:14px;z-index:var(--ckp-z);display:none;align-items:center;cursor:pointer;user-select:none;-webkit-tap-highlight-color:transparent;}',
      '.ckp-front{position:relative;z-index:2;display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #e3e3e3;border-radius:999px;padding:10px 14px;box-shadow:0 6px 22px rgba(0,0,0,.16);}',
      '#ckp-pill svg{flex:none;}',
      '#ckp-pill .t{font-size:12px;font-weight:700;color:#111;line-height:1.2;white-space:nowrap;max-width:176px;overflow:hidden;text-overflow:ellipsis;}',
      '#ckp-pill .c{font-size:12px;color:#888;}',
      /* A real flex sibling, not an absolutely-positioned overlay. The first
         version stretched the whole width of the pill and tucked underneath
         it, which meant its centre point — where a thumb actually aims —
         was covered by the white board and unclickable. It is now a short
         board whose visible left end IS its centre. */
      /* Geometry derives from CFG.gamePeek so there is one number to tune.
         The arrow now lives inside a white disc: the disc is what gets centred
         in the exposed area, which removes the alignment ambiguity entirely —
         a circle has no optical bias, unlike a bare triangle. */
      '.ckp-back{position:relative;z-index:1;flex:none;width:' + (CFG.gamePeek + CFG.gameOverlap) + 'px;' +
        'margin-right:-' + CFG.gameOverlap + 'px;' +
        /* full height, flush top and bottom with the white board. Insetting it
           by a few px read as a misalignment rather than as depth — the colour,
           the z-order and the shadow already say "behind". Equal heights also
           make the two capsules share a radius, so the exposed end looks like
           the end of the same kind of object. */
        'align-self:stretch;' +
        /* Round on the outer edge, square where it meets the white board, so
           it reads as a longer board running underneath rather than as a
           separate capsule stuck to the side. A fully-rounded right edge was
           the thing that made it look like a blob. */
        'background:' + CFG.gameAccent + ';border-radius:999px 0 0 999px;display:flex;align-items:center;' +
        'padding-left:' + Math.round((CFG.gamePeek - Math.round(CFG.gamePeek * 0.70)) / 2) + 'px;' +
        'box-shadow:0 4px 14px rgba(0,0,0,.22);cursor:pointer;' +
        'transition:transform .16s ease,margin-right .16s ease;}',
      '.ckp-back:active{transform:scale(.96);margin-right:-' + (CFG.gameOverlap - 4) + 'px;}',
      /* arrow scaled to the exposed sliver, biased left because a right-pointing
         triangle's optical centre sits left of its bounding box */
      '.ckp-disc{flex:none;width:' + Math.round(CFG.gamePeek * 0.70) + 'px;height:' + Math.round(CFG.gamePeek * 0.70) + 'px;' +
        'border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;}',
      /* the triangle still gets its 1px nudge — inside the disc this time,
         where it is a small correction rather than the whole problem */
      '.ckp-disc svg{width:' + Math.round(CFG.gamePeek * 0.28) + 'px;height:' + Math.round(CFG.gamePeek * 0.30) + 'px;' +
        'transform:translateX(' + Math.max(1, Math.round(CFG.gamePeek * 0.03)) + 'px);display:block;}',
      '.ckp-back.busy{opacity:.55;}',
      '@media(prefers-reduced-motion:reduce){#ckp-ov,.ckp-sheet{transition:none;}}'
    ].join("");
    document.head.appendChild(s);
  }

  var X_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round"><path d="M7 7l10 10M17 7L7 17"/></svg>';

  /* ── Overlay scaffolding ─────────────────────────────────────── */
  var _lastOverlay = null;
  function openOverlay(sheetHtml, label) {
    injectCss();
    closeOverlay();
    var ov = document.createElement("div");
    ov.id = "ckp-ov";
    ov.innerHTML = '<div class="ckp-sheet" role="dialog" aria-modal="true" tabindex="-1" aria-label="' + esc(label) + '">' +
      '<div class="ckp-grab"></div>' +
      '<button class="ckp-x" aria-label="Close">' + X_SVG + '</button>' + sheetHtml + "</div>";
    document.body.appendChild(ov);
    document.documentElement.style.overflow = "hidden";
    requestAnimationFrame(function () { requestAnimationFrame(function () { ov.classList.add("on"); }); });
    function close() { closeOverlay(); if (_onClose) _onClose(); }
    ov.addEventListener("click", function (e) { if (e.target === ov) close(); });
    ov.querySelector(".ckp-x").addEventListener("click", close);
    document.addEventListener("keydown", escHandler);
    function escHandler(e) { if (e.key === "Escape") { close(); document.removeEventListener("keydown", escHandler); } }
    _lastOverlay = ov;
    /* Move focus INTO the dialog, not onto its close button. Focusing the
       button was what put a ring on it every time the sheet opened; focusing
       the sheet itself gives assistive tech the same context with nothing
       drawn. */
    var sheetEl = ov.querySelector(".ckp-sheet");
    if (sheetEl) try { sheetEl.focus({ preventScroll: true }); } catch (e) {}
    return ov;
  }
  function closeOverlay() {
    if (_lastOverlay) { _lastOverlay.remove(); _lastOverlay = null; }
    document.documentElement.style.overflow = "";
  }
  var _onClose = null;

  /* ══════════════════════════════════════════════════════════════
     Minimised pill

     v2.3 adds an optional ▶ that starts the game. It is a sibling of the
     label, not a wrapper, and stops propagation — tapping ▶ plays, tapping
     anywhere else on the pill still does what it always did.
     ══════════════════════════════════════════════════════════════ */
  function gameEnabled() {
    return !!(CFG.gameOn && GAME_URL && !window.CK_GAME_OFF && !MOCK);
  }

  /* opts.pickThenPlay — iOS visitors who skipped the picker have no model,
     so v2.3 gave them a pill with no play button at all. That is the "game
     doesn't work on iPhone" report: on iOS the game was simply unreachable
     unless you had already chosen a model. Now the arrow is always there;
     if we don't know the model yet it opens the picker and goes straight
     into the game once they choose. */
  function showPill(text, onTap, entry, opts) {
    injectCss();
    opts = opts || {};
    var p = document.getElementById("ckp-pill");
    if (!p) {
      p = document.createElement("div");
      p.id = "ckp-pill";
      document.body.appendChild(p);
    }
    var playable = gameEnabled() && (!!entry || !!opts.pickThenPlay);
    p.className = playable ? "playable" : "";
    p.innerHTML =
      (playable
        ? '<span class="ckp-back" role="button" tabindex="0" aria-label="Play the case game">' +
          '<span class="ckp-disc"><svg viewBox="0 0 9 10" fill="' + CFG.gameAccent + '">' +
          '<path d="M0 0l9 5-9 5z"/></svg></span></span>'
        : "") +
      '<span class="ckp-front">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" stroke-width="2"><rect x="6" y="2" width="12" height="20" rx="2.5"/><line x1="10" y1="19" x2="14" y2="19"/></svg>' +
      '<span class="t">' + esc(text) + '</span><span class="c">›</span></span>';
    p.style.display = "flex";

    var front = p.querySelector(".ckp-front");
    if (front) front.onclick = function () {
      track("mp_pill_cta", { model: entry ? entry.n : "" });
      p.style.display = "none";
      onTap();
    };
    if (playable) {
      var btn = p.querySelector(".ckp-back");
      if (btn) btn.addEventListener("click", function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (entry) { startGame(entry, btn); return; }
        /* no model yet → pick one, then play */
        track("mp_game_needs_model");
        _playAfterPick = true;
        p.style.display = "none";
        loadData().then(function (d) { showPicker(d); }).catch(function () {
          _playAfterPick = false;
          showPill(text, onTap, entry, opts);
        });
      });
    }
    track("mp_pill_shown", { model: entry ? entry.n : "", playable: playable ? 1 : 0 });
    _pillHidByObstruction = false;
    requestAnimationFrame(function () { checkObstruction(); startPillWatch(); });
    return p;
  }
  function hidePill() { var p = document.getElementById("ckp-pill"); if (p) p.style.display = "none"; }

  /* ══════════════════════════════════════════════════════════════
     Obstruction watch

     The pill is position:fixed bottom-right, and it was landing on top of
     the side-cart drawer's checkout button. Covering a checkout button is
     the single most expensive thing a widget can do, and no amount of path
     suppression catches it: the drawer is an overlay on ordinary pages, not
     a URL. The theme also renders a sticky add-to-cart bar on product pages,
     which is the same class of collision.

     Rather than hard-code this theme's drawer selectors — which would break
     the next time the theme changes — this asks the only question that
     matters: is anything actually on top of the pill? elementFromPoint at
     the pill's resting centre answers it directly and works against any
     theme, any app, any third-party widget.

     Two outcomes, because they deserve different treatment:
       • a short bar pinned to the bottom (sticky ATC) → lift the pill above it
       • anything larger (a drawer, a modal) → hide the pill entirely
     The hit test always uses the RESTING centre, never the lifted one, or
     lifting would clear the obstruction, drop it back, and oscillate.
     ══════════════════════════════════════════════════════════════ */
  var _pillWatch = null, _pillSize = null, _pillAnchor = null,
      _pillLift = 0, _pillHidByObstruction = false;

  /* v2.5.1 — two corrections to v2.5.0, which did not work on the live store:

     1. WRONG QUESTION. v2.5.0 asked "is anything covering the pill?" via
        elementFromPoint. The pill is z-index 99999, so it is the topmost
        element at its own centre and the answer was always no. The question
        that matters is the opposite one: "is the pill covering something?"
        elementsFromPoint (plural) returns the whole stack at that point, so
        we can skip past ourselves and look at what is underneath.
        v2.5.0's test only passed because the fake drawer in it shared the
        pill's z-index and won on DOM order — a flattering test, not a real one.

     2. innerHeight AGAIN. The anchor point was computed from
        window.innerHeight, which is the layout viewport, while a fixed
        element is placed against the visual viewport. Same mismatch that
        broke the paddle. The anchor is now taken from a real
        getBoundingClientRect measurement, captured while the pill is at rest
        so that lifting it cannot move the probe and cause oscillation. */
  /* Collect EVERY fixed element sitting under the probe point, not just the
     topmost one.

     v2.5.1 returned the first fixed ancestor it found and stopped. On the
     live store that was Shopify's preview bar, which sits above the cart
     drawer at the same point — so the drawer was never seen and the pill
     happily lifted 78px and parked itself over the drawer instead. Any
     "topmost wins" rule has this hole: a harmless bar can mask a harmful
     overlay directly behind it.

     Visibility is judged on EFFECTIVE opacity up the ancestor chain, because
     a drawer parked off-stage with opacity:0 is still returned by
     elementsFromPoint and would otherwise read as a permanent obstruction —
     which would silently kill the pill everywhere. (display:none and
     visibility:hidden are already excluded by elementsFromPoint itself.) */
  function fixedUnder(p, pt) {
    var out = [], seen = [];
    var stack;
    try {
      if (document.elementsFromPoint) stack = document.elementsFromPoint(pt.x, pt.y) || [];
      else {
        var prev = p.style.pointerEvents;
        p.style.pointerEvents = "none";
        var one = document.elementFromPoint(pt.x, pt.y);
        p.style.pointerEvents = prev;
        stack = one ? [one] : [];
      }
    } catch (e) { return out; }

    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (!el || el === p || (p.contains && p.contains(el))) continue;
      var alpha = 1, fx = null;
      for (var n = el; n && n !== document.documentElement; n = n.parentElement) {
        if (n === p) { fx = null; break; }
        if (n.id === "ckp-ov" || n.id === "ckg-layer") return "OURS";
        var st;
        try { st = getComputedStyle(n); } catch (e) { break; }
        var o = parseFloat(st.opacity);
        if (!isNaN(o)) alpha *= o;
        if (st.position === "fixed") { fx = n; break; }
      }
      if (!fx || alpha <= 0.1) continue;
      if (seen.indexOf(fx) !== -1) continue;
      var r = fx.getBoundingClientRect();
      if (r.width < 8 || r.height < 8) continue;
      seen.push(fx);
      out.push({ el: fx, rect: r });
    }
    return out;
  }

  function checkObstruction() {
    var p = document.getElementById("ckp-pill");
    if (!p) { stopPillWatch(); return; }
    if (_lastOverlay || document.getElementById("ckg-layer")) return;  /* we are on screen ourselves */

    var shown = p.style.display !== "none";
    if (shown && _pillLift === 0) {
      /* measure at rest only — a lifted pill would move the probe */
      var r = p.getBoundingClientRect();
      if (r.width) {
        _pillSize = { w: r.width, h: r.height };
        _pillAnchor = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
    }
    if (!_pillAnchor) return;

    var found = fixedUnder(p, _pillAnchor);
    if (found === "OURS") return;

    /* A short bar pinned to the bottom is something to sit above. Anything
       else at this point is a drawer or a modal, and hiding wins outright —
       being wrong in that direction costs a glance, being wrong the other
       way covers a checkout button. */
    var vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
    var hide = false, lift = 0;
    for (var fi = 0; fi < found.length; fi++) {
      var fr = found[fi].rect;
      if ((vh - fr.bottom) < 12 && fr.height < vh * 0.35) {
        lift = Math.max(lift, Math.round(fr.height + 10));
      } else {
        hide = true;
      }
    }

    if (hide) {
      if (shown) { p.style.display = "none"; _pillHidByObstruction = true; track("mp_pill_obstructed"); }
    } else {
      if (!shown && _pillHidByObstruction) { p.style.display = "flex"; _pillHidByObstruction = false; }
      if (lift !== _pillLift) {
        _pillLift = lift;
        p.style.bottom = (CFG.pillBottom + lift) + "px";
      }
    }
  }

  function startPillWatch() {
    if (_pillWatch) return;
    _pillWatch = setInterval(checkObstruction, 500);
  }
  function stopPillWatch() {
    if (_pillWatch) { clearInterval(_pillWatch); _pillWatch = null; }
  }

  /* ── Products fetch ──────────────────────────────────────────── */
  /* v2.7 — in stock only. Collections are best-selling first, and a model can
     be switched off ("stop selling", stock −100) while its products stay
     listed: v2.6 showed Vivo V60e owners three sold-out top sellers. Fetch a
     few more than needed (12 is about 8 KB on the wire) and keep the ones
     that can be bought. One request per collection per page. */
  var _listCache = {};
  function inStock(p) {
    var vs = (p && p.variants) || [];
    for (var i = 0; i < vs.length; i++) if (vs[i].available) return true;
    return false;
  }
  function fetchProducts(handle, limit) {
    if (MOCK && MOCK.products) return Promise.resolve(MOCK.products);
    var n = limit || 3, ask = n <= 4 ? 12 : Math.min(250, n * 3), key = handle + "|" + ask;
    if (!_listCache[key]) {
      _listCache[key] = fetch(location.origin + "/collections/" + handle + "/products.json?limit=" + ask)
        .then(function (r) { return r.json(); })
        .then(function (d) { return (d.products || []).filter(inStock); });
    }
    return _listCache[key].then(function (list) { return list.slice(0, n); });
  }
  function glassAvailable(entry) {
    if (MOCK) return Promise.resolve(!!MOCK.glassOk);
    if (!entry.gh || !entry.gu) return Promise.resolve(false);
    return fetchProducts(entry.gh, 1)
      .then(function (l) { return l.length > 0; })
      .catch(function () { return false; });
  }
  /* A phone with nothing in stock gets no popup and no pill — remembered for
     the visit. Slow or failed fetch counts as in stock (fails open). */
  function modelInStock(entry) {
    if (MOCK || !entry.h) return Promise.resolve(true);
    var key = "ck_stock_" + entry.h;
    try { var c = sessionStorage.getItem(key); if (c) return Promise.resolve(c === "1"); } catch (e) {}
    return withTimeout(fetchProducts(entry.h, 3).then(function (l) { return l.length > 0; }), CFG.fetchTimeout, null)
      .then(function (ok) {
        if (ok === null) return true;
        try { sessionStorage.setItem(key, ok ? "1" : "0"); } catch (e) {}
        return ok;
      });
  }
  function withTimeout(promise, ms, fallback) {
    return new Promise(function (res) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; res(fallback); } }, ms);
      promise.then(function (v) { if (!done) { done = true; clearTimeout(t); res(v); } })
             .catch(function () { if (!done) { done = true; clearTimeout(t); res(fallback); } });
    });
  }

  /* ── Match popup ─────────────────────────────────────────────── */
  function productCardHtml(p, isBest, url) {
    var img = p.images && p.images[0] ? p.images[0].src : (p.image || "");
    var v   = p.variants && p.variants[0] ? p.variants[0] : null;
    var pr  = v ? money(v.price) : "";
    var cmp = v && v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price) ? money(v.compare_at_price) : "";
    return '<a class="ckp-card" href="' + esc(url) + '">' +
      (isBest ? '<span class="ckp-tag">Bestseller</span>' : "") +
      '<div class="ckp-img">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : "") + "</div>" +
      '<div class="ckp-meta"><p class="ckp-name">' + esc(p.title || "") + '</p>' +
      '<p class="ckp-price">' + pr + (cmp ? '<span class="ckp-cmp">' + cmp + "</span>" : "") + "</p></div></a>";
  }

  /* v2.6 — "this design for your phone", answered by Ck-switch-model.js on
     product pages. Anything else (no switcher, not a design page, their own
     phone, lookup failed) resolves null and the popup is unchanged. */
  function thisDesign(entry) {
    if (MOCK) return Promise.resolve(MOCK.thisDesign || null);
    try {
      var sw = window.__ckSwitch;
      if (sw && typeof sw.mineFor === "function") {
        return Promise.resolve(sw.mineFor(entry)).then(function (m) { return m && m.url ? m : null; }, function () { return null; });
      }
    } catch (e) {}
    return Promise.resolve(null);
  }
  function thisDesignCardHtml(m) {
    return '<a class="ckp-card ckp-this" href="' + esc(m.url) + '">' +
      '<span class="ckp-tag">This design</span>' +
      '<div class="ckp-img">' + (m.img ? '<img src="' + esc(m.img) + '" alt="" loading="lazy">' : "") + "</div>" +
      '<div class="ckp-meta"><p class="ckp-name">' + esc(m.title || "") + '</p>' +
      '<p class="ckp-price">' + esc(m.price || "") + (m.cmp ? '<span class="ckp-cmp">' + esc(m.cmp) + "</span>" : "") + "</p></div></a>";
  }

  function showMatchPopup(entry, opts) {
    opts = opts || {};
    var auto = !!opts.auto;
    if (auto) opts = { iphone: opts.iphone };   // re-opens from the pill are taps, not auto
    var model = entry.n;
    var tdP = thisDesign(entry);
    Promise.all([
      withTimeout(fetchProducts(entry.h, 3), CFG.fetchTimeout, []),
      withTimeout(glassAvailable(entry), CFG.fetchTimeout, false),
      withTimeout(tdP, CFG.thisDesignWait, null)
    ]).then(function (rs) {
      var products = rs[0] || [], hasGlass = rs[1], td = rs[2];
      /* v2.7: never open an empty sheet by itself (nothing in stock to show,
         or the fetch was too slow). A tap on the pill still opens it. */
      if (auto && !products.length && !td) { track("mp_suppressed", { model: model, why: "no_products" }); return; }
      var rowTitle = model.length > 16 ? "Top selling cases" : "Top selling for " + esc(model);
      var spDisp = String(entry.sp || "50+");
      if (spDisp.slice(-1) !== "+") spDisp += "+";
      /* With "this design" first, two top sellers follow (never the same product twice). */
      function gridHtml(t) {
        var list = t ? products.filter(function (p) { return p.handle !== t.handle; }).slice(0, 2) : products;
        if (!list.length && !t) return "";
        return '<div class="ckp-row"><p class="ckp-rowt">' + (t ? "This design + top sellers" : rowTitle) + '</p><a class="ckp-seeall" href="' + esc(entry.u) + '">See all →</a></div>' +
          '<div class="ckp-grid">' + (t ? thisDesignCardHtml(t) : "") +
          list.map(function (p, i) { return productCardHtml(p, i === (t ? 0 : 1), entry.u); }).join("") + "</div>" +
          '<p class="ckp-proof"><b>' + esc(spDisp) + '</b> bought this month</p>';
      }
      var grid = gridHtml(td);
      var ctaLabel = model.length > 18 ? "View all cases" : "Shop " + esc(model) + " cases";
      var footHtml = opts.iphone
        ? '<a id="ckp-change">Wrong model? <span>Change</span></a>'
        : '<a href="/pages/phone-cases-by-model">Not your phone? <span>Choose your model</span></a>';

      var html =
        '<div class="ckp-head"><p class="ckp-eyebrow">Made for your phone</p>' +
        '<h3 class="ckp-title">' + esc(model) + "</h3></div>" +
        '<div class="ckp-body">' + grid + "</div>" +
        '<div class="ckp-ctas"><a class="ckp-btn" href="' + esc(entry.u) + '">' + ctaLabel + " →</a>" +
        (hasGlass ? '<a class="ckp-btn2" href="' + esc(entry.gu) + '">Premium glass cases</a>' : "") +
        "</div>" +
        '<div class="ckp-foot">' + footHtml + "</div>";

      _onClose = function () {
        track("mp_close", { model: model });
        showPill(model + " cases", function () { reopen(); }, entry);
      };
      function reopen() { showMatchPopup(entry, opts); }

      var ov = openOverlay(html, "Cases for " + model);
      hidePill();
      track("mp_shown", { model: model });
      function wire(a) {
        a.addEventListener("click", function () {
          if (a.className.indexOf("ckp-this") !== -1) {
            track("mp_this_design", { model: model });
            try { sessionStorage.setItem("ck_sw_used", "pop"); } catch (e) {}
          } else {
            track(a.className.indexOf("btn2") !== -1 ? "mp_glass" : "mp_cta", { model: model });
          }
          closeOverlay();
          showPill(model + " cases", reopen, entry);
        });
      }
      ov.querySelectorAll(".ckp-btn,.ckp-btn2,.ckp-card").forEach(wire);
      if (td) track("mp_this_design_shown", { model: model, late: 0 });
      else tdP.then(function (late) {
        /* arrived after the sheet opened: slot it in if the sheet is still up */
        if (!late || _lastOverlay !== ov) return;
        var body = ov.querySelector(".ckp-body");
        if (!body) return;
        body.innerHTML = gridHtml(late);
        body.querySelectorAll(".ckp-card").forEach(wire);
        track("mp_this_design_shown", { model: model, late: 1 });
      });
      var chg = ov.querySelector("#ckp-change");
      if (chg) chg.addEventListener("click", function () {
        try { localStorage.removeItem(K.iModel); } catch (e) {}
        closeOverlay();
        loadData().then(function (d) { showPicker(d); });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════════
     v2.3 — the game

     Everything here fails open. If the asset 404s, the products fetch is
     slow, or CKGame throws, the visitor lands back on the ordinary pill and
     the popup behaves exactly as it did in v2.2. A broken game must never
     cost a session.
     ══════════════════════════════════════════════════════════════ */

  /* Paddle label. The full name does not fit: "iPhone 15 Pro Max" only just
     clears a 140px paddle, and "Samsung Galaxy S24 Ultra 5G" never will.
     Runtime rule for now — the correct fix is a short-label field written by
     the Apps Script into ck-popup-data.json, once this has earned it. */
  function shortLabel(name) {
    var s = String(name || "").trim();
    if (s.length <= 16) return s;
    s = s.replace(/\s*\b5G\b\s*$/i, "").trim();
    if (s.length <= 16) return s;
    s = s.replace(/^(Samsung|Xiaomi|Realme|Motorola|OnePlus|Google)\s+/i, "").trim();
    return s;
  }

  var _gamePromise = null;
  function loadGame() {
    if (_gamePromise) return _gamePromise;
    if (window.CKGame) { _gamePromise = Promise.resolve(true); return _gamePromise; }
    _gamePromise = new Promise(function (res, rej) {
      var s = document.createElement("script");
      s.src = GAME_URL;
      s.async = true;
      s.onload = function () { res(!!window.CKGame); };
      s.onerror = function () { rej(new Error("ck: game asset failed")); };
      document.head.appendChild(s);
    });
    return _gamePromise;
  }

  /* Map raw Shopify products to the flat shape the game wants, and shrink
     the images — these are drawn at ~85px wide on a phone, there is no
     reason to pull full-size assets over a mobile connection. */
  var _gameProdCache = {};
  function gameProducts(entry) {
    if (_gameProdCache[entry.h]) return Promise.resolve(_gameProdCache[entry.h]);
    return fetchProducts(entry.h, CFG.gameProducts).then(function (list) {
      return (list || []).map(function (p) {
        var v = p.variants && p.variants[0] ? p.variants[0] : null;
        var im = p.images && p.images[0] ? p.images[0].src : (p.image || "");
        return {
          title: p.title || "",
          price: v ? money(v.price) : "",
          img: thumb(im, 260),
          handle: p.handle || ""
        };
      });
    }).then(function (mapped) {
      if (mapped.length) _gameProdCache[entry.h] = mapped;
      return mapped;
    });
  }

  function startGame(entry, btn) {
    if (!gameEnabled() || !entry) return;
    if (btn) { if (btn.classList.contains("busy")) return; btn.classList.add("busy"); }
    track("mp_game_tap", { model: entry.n });

    var pill = document.getElementById("ckp-pill");
    if (!pill) return;

    function bail(why) {
      track("mp_game_unavailable", { model: entry.n, why: why });
      if (btn) btn.classList.remove("busy");
      showPill(entry.n + " cases", function () { showMatchPopup(entry, {}); }, entry);
    }

    withTimeout(
      Promise.all([loadGame(), gameProducts(entry)]),
      CFG.gameTimeout,
      null
    ).then(function (rs) {
      if (btn) btn.classList.remove("busy");
      if (!rs) return bail("timeout");
      var prods = rs[1] || [];
      if (!window.CKGame) return bail("noasset");
      if (prods.length < CFG.gameMinProducts) return bail("thin");

      track("mp_game_start", { model: entry.n, bricks: prods.length });
      return window.CKGame.play({
        pill: pill,
        products: prods,
        label: shortLabel(entry.n),
        track: track
      }).then(function (res) {
        track("mp_game_end", {
          model: entry.n,
          cleared: res.cleared ? 1 : 0,
          quit: res.quit ? 1 : 0,
          broken: res.broken.length,
          secs: res.secs
        });
        stampCart(entry, res);
        logPlay(entry, res);
        showEndSheet(entry, res);
      });
    }).catch(function () { bail("error"); });
  }

  /* ══════════════════════════════════════════════════════════════
     Measurement

     Pixel events tell you how many people played. They cannot tell you
     whether players BUY more, because they don't survive into the order.
     Cart attributes do: whatever is set here rides the cart into Shopify's
     order record, so "did people who played convert better, and at what
     AOV" becomes an order filter/export rather than an attribution guess.

     Fired once per finished round. It creates an empty cart for players who
     never add anything — harmless (a cart is not a checkout, and abandoned-
     checkout flows only trigger at checkout), but that is the trade, and
     CFG.gameCartTag turns it off.
     ══════════════════════════════════════════════════════════════ */
  function stampCart(entry, res) {
    if (!CFG.gameCartTag || MOCK) return;
    try {
      fetch(location.origin + "/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            /* ck-android-detector.js already stores this and already tags the
               cart with it — reusing it means a play row and an order can be
               joined without inventing a second identifier. */
            "ck_session_id": (function () {
              try { return localStorage.getItem("ck_session_id") || ""; } catch (e) { return ""; }
            })(),
            "ck_played_game": "1",
            "ck_game_model": String(entry.n || ""),
            "ck_game_result": res.cleared ? "cleared" : (res.quit ? "quit" : "lost"),
            "ck_game_smashed": String((res.broken || []).length)
          }
        })
      }).catch(function () {});
    } catch (e) {}
  }

  /* Optional raw-row log, for the Google Sheet. Deliberately only wired to
     round completions — a few hundred rows a day. Do NOT point this at
     mp_pill_shown: that fires on most mobile sessions and would burn the
     Apps Script quota and the sheet's cell limit within days. */
  function logPlay(entry, res) {
    if (!CFG.gameLogUrl || MOCK) return;
    try {
      var sid = "";
      try { sid = localStorage.getItem("ck_session_id") || ""; } catch (e) {}
      var q = "?session_id=" + encodeURIComponent(sid) +
              "&model=" + encodeURIComponent(entry.n || "") +
              "&cleared=" + (res.cleared ? 1 : 0) +
              "&quit=" + (res.quit ? 1 : 0) +
              "&smashed=" + ((res.broken || []).length) +
              "&secs=" + (res.secs || 0) +
              "&score=" + (res.score || 0) +
              "&best=" + (res.best || 0);
      /* sendBeacon survives the page unload that follows a CTA tap; the
         Apps Script response is opaque and we don't care about it. */
      if (navigator.sendBeacon) navigator.sendBeacon(CFG.gameLogUrl + q);
      else new Image().src = CFG.gameLogUrl + q;
    } catch (e) {}
  }

  function smashCardHtml(p, url) {
    return '<a class="ckp-card" href="' + esc(url) + '">' +
      '<div class="ckp-img">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : "") + "</div>" +
      '<div class="ckp-meta"><p class="ckp-name">' + esc(p.title) + '</p>' +
      '<p class="ckp-price">' + esc(p.price) + "</p></div></a>";
  }

  /* End sheet is rendered here, not in the game asset, so it uses exactly
     the same components as the match popup and there is one copy of the CSS. */
  function showEndSheet(entry, res) {
    var model = entry.n;
    var broken = res.broken || [];
    var list = broken.slice(0, 3);
    var cleared = res.cleared;

    var title = broken.length
      ? "You unlocked " + broken.length + " case" + (broken.length > 1 ? "s" : "")
      : "Nothing unlocked";
    var eyebrow = cleared ? "Board cleared" : (res.quit ? "Game ended" : "Out of lives");
    var rowTitle = broken.length ? "Cases you smashed" : "Top selling cases";

    var grid = list.length
      ? '<div class="ckp-row"><p class="ckp-rowt">' + esc(rowTitle) + '</p>' +
        '<a class="ckp-seeall" href="' + esc(entry.u) + '">See all →</a></div>' +
        '<div class="ckp-grid">' + list.map(function (p) { return smashCardHtml(p, entry.u); }).join("") + "</div>" +
        '<p class="ckp-proof">Smashed in <b>' + res.secs + 's</b></p>'
      : "";

    /* Reward. Deliberately gated on CLEARING the board, not on playing —
       a participation prize is just a discount with extra steps. Unset by
       default; configure with window.CK_POPUP_CFG.gameCoupon in theme.liquid
       so it can be switched on and off without touching this asset. */
    var cp = CFG.gameCoupon;
    var rew = (cp && cp.code && cleared)
      ? '<div class="ckp-rew"><p class="ckp-rewt">' + esc(cp.label || "Reward unlocked") + '</p>' +
        '<p class="ckp-code" id="ckp-code">' + esc(cp.code) +
        '<small>' + esc(cp.value || "") + '</small></p>' +
        (cp.terms ? '<p class="ckp-rewh">' + esc(cp.terms) + '</p>' : '') + '</div>'
      : "";

    var ctaLabel = model.length > 18 ? "View all cases" : "Shop " + esc(model) + " cases";
    var html =
      '<div class="ckp-head"><p class="ckp-eyebrow ' + (cleared ? "win" : "lose") + '">' + esc(eyebrow) + '</p>' +
      '<h3 class="ckp-title">' + esc(title) + "</h3></div>" +
      '<div class="ckp-body">' + grid + rew + "</div>" +
      '<div class="ckp-ctas"><a class="ckp-btn" href="' + esc(entry.u) + '">' + ctaLabel + " →</a>" +
      '<button class="ckp-btn2" id="ckp-again">Play again</button></div>' +
      '<div class="ckp-foot"><a id="ckp-back">No thanks, <span>back to browsing</span></a></div>';

    _onClose = function () {
      showPill(model + " cases", function () { showMatchPopup(entry, {}); }, entry);
    };

    var ov = openOverlay(html, "Game results for " + model);
    hidePill();

    ov.querySelector(".ckp-btn").addEventListener("click", function () {
      track("mp_game_cta", { model: model, broken: broken.length });
    });
    var again = ov.querySelector("#ckp-again");
    if (again) again.addEventListener("click", function () {
      track("mp_game_again", { model: model });
      closeOverlay();
      showPill(model + " cases", function () { showMatchPopup(entry, {}); }, entry);
      var p = document.getElementById("ckp-pill");
      startGame(entry, p ? p.querySelector(".ckp-back") : null);
    });
    var codeEl = ov.querySelector("#ckp-code");
    if (codeEl) {
      track("mp_game_reward_shown", { model: model, code: cp.code });
      codeEl.addEventListener("click", function () {
        try {
          if (navigator.clipboard) navigator.clipboard.writeText(cp.code);
        } catch (e) {}
        track("mp_game_reward_copy", { model: model, code: cp.code });
        var sm = codeEl.querySelector("small");
        if (sm) sm.textContent = "copied";
      });
    }
    var back = ov.querySelector("#ckp-back");
    if (back) back.addEventListener("click", function () { closeOverlay(); _onClose(); });
  }

  /* ── iPhone picker ───────────────────────────────────────────── */
  /* Series grouping is fully dynamic: any new model name coming from the
     data file creates/joins its group automatically — no code change needed
     when new iPhones launch. */
  function seriesOf(name) {
    var m = name.match(/^iPhone\s+([A-Za-z0-9]+)/i);
    if (!m) return "More models";
    var t = m[1].toUpperCase();
    if (t === "X" || t === "XS" || t === "XR") return "iPhone X series";
    var num = parseInt(t, 10);
    if (!isNaN(num)) return num >= 11 ? "iPhone " + num : "Older models";
    return "iPhone " + m[1].charAt(0).toUpperCase() + m[1].slice(1);  // Air, Fold, …
  }

  function showPicker(data) {
    var order = (data.iphone && data.iphone.order) || [];
    var map   = (data.iphone && data.iphone.map)   || {};
    var groups = [], seen = {};
    order.forEach(function (name) {
      var s = seriesOf(name);
      if (!seen[s]) { seen[s] = { label: s, items: [] }; groups.push(seen[s]); }
      seen[s].items.push(name);
    });
    var listHtml = groups.map(function (g) {
      return '<p class="ckp-series">' + esc(g.label) + '</p><div class="ckp-chips">' +
        g.items.map(function (n) { return '<button class="ckp-chip" data-m="' + esc(n) + '">' + esc(n) + "</button>"; }).join("") +
        "</div>";
    }).join("");

    var html =
      '<div class="ckp-head"><p class="ckp-eyebrow">CaseKaro</p>' +
      '<h3 class="ckp-title">Which iPhone do you have?</h3>' +
      '<p class="ckp-sub">Pick your model to see perfect-fit cases</p></div>' +
      '<div class="ckp-plist">' + listHtml + "</div>" +
      '<div class="ckp-foot"><a id="ckp-skip">Skip for now</a></div>';

    _onClose = function () {
      try { sessionStorage.setItem(K.iSkip, "1"); } catch (e) {}
      track("mp_picker_skip");
      showPill("Find your iPhone case", function () {
        loadData().then(function (d) { showPicker(d); });
      }, null, { pickThenPlay: true });
    };

    var ov = openOverlay(html, "Choose your iPhone model");
    hidePill();
    track("mp_picker_shown");
    ov.querySelector("#ckp-skip").addEventListener("click", function () { closeOverlay(); _onClose(); });
    ov.querySelectorAll(".ckp-chip").forEach(function (b) {
      b.addEventListener("click", function () {
        var name  = b.getAttribute("data-m");
        var entry = map[name.toLowerCase()];
        if (!entry) return;
        entry = { n: name, u: entry.u, h: entry.h, gu: entry.gu, gh: entry.gh, sp: entry.sp };
        try {
          localStorage.setItem(K.iModel, JSON.stringify({ m: entry, exp: Date.now() + CFG.rememberDays * 864e5 }));
          sessionStorage.setItem(K.seen, "1");
        } catch (e) {}
        track("mp_picker_pick", { model: name, toGame: _playAfterPick ? 1 : 0 });
        closeOverlay();
        if (_playAfterPick) {
          _playAfterPick = false;
          showPill(name + " cases", function () { showMatchPopup(entry, { iphone: true }); }, entry);
          var pp = document.getElementById("ckp-pill");
          startGame(entry, pp ? pp.querySelector(".ckp-back") : null);
          return;
        }
        showMatchPopup(entry, { iphone: true });
      });
    });
  }

  /* ── Flow control ────────────────────────────────────────────── */
  /* v2.1: the whole surface — popup AND pill — is gated behind
     shouldStaySilent(), so an own-model page renders nothing.
     K.seen is deliberately left untouched when we stay silent, so the
     visitor still gets the full popup once (not the pill) on the next
     page that isn't about their own phone. */
  /* v2.7: the model index is loaded first (the data file is cached by then),
     so collection and product handles can be read for "is this their phone";
     the page gate and the in-stock check run before anything is drawn. */
  function runMatchFlow(entry, iphone) {
    var t0 = Date.now();
    loadData().then(function (d) { buildIndex(d); }, function () {}).then(function () {
      return shouldStaySilent(entry);
    }).then(function (why) {
      if (why) {
        if (why === "own" || why === "switched") track("mp_suppressed_own_model", { model: entry.n });
        else track("mp_suppressed", { model: entry.n, why: why });
        return;
      }
      /* the checks above already took time; wait only what is left of delayMatch */
      setTimeout(function () {
        var seenNow = false;
        try { seenNow = !!sessionStorage.getItem(K.seen); } catch (e) {}
        if (!seenNow) {
          try { sessionStorage.setItem(K.seen, "1"); } catch (e) {}
          showMatchPopup(entry, { iphone: iphone, auto: true });
        } else {
          showPill(entry.n + " cases", function () { showMatchPopup(entry, { iphone: iphone }); }, entry);
        }
      }, Math.max(0, CFG.delayMatch - (Date.now() - t0)));
    });
  }

  /* Android */
  function androidInit() {
    var cached = null;
    try { cached = sessionStorage.getItem(K.match); } catch (e) {}
    if (cached) { try { runMatchFlow(JSON.parse(cached), false); return; } catch (e) {} }

    function uaModel() {
      var m = UA.match(/; ([^;]+) Build\//) || UA.match(/\(([^;]+);/);
      return m && m[1] ? m[1].trim() : null;
    }
    function lookup(hw) {
      if (!hw) return;
      loadData().then(function (d) {
        var entry = (d.android || {})[hw.toLowerCase()];
        if (!entry) return;
        try { sessionStorage.setItem(K.match, JSON.stringify(entry)); } catch (e) {}
        runMatchFlow(entry, false);
      }).catch(function () {});
    }
    if (MOCK && MOCK.entry) {
      try { sessionStorage.setItem(K.match, JSON.stringify(MOCK.entry)); } catch (e) {}
      runMatchFlow(MOCK.entry, false); return;
    }
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      navigator.userAgentData.getHighEntropyValues(["model"])
        .then(function (u) { lookup(u.model || uaModel()); })
        .catch(function () { lookup(uaModel()); });
    } else { lookup(uaModel()); }
  }

  /* iPhone */
  function iphoneInit() {
    var saved = null;
    try {
      var raw = localStorage.getItem(K.iModel);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && obj.exp > Date.now() && obj.m) saved = obj.m;
        else localStorage.removeItem(K.iModel);
      }
    } catch (e) {}

    if (saved) { runMatchFlow(saved, true); return; }

    var skipped = false;
    try { skipped = !!sessionStorage.getItem(K.iSkip); } catch (e) {}

    /* v2.2: both surfaces wait for the data file, so we can ask whether this
       page already names a model before interrupting anyone.
       v2.7: and the page gate — no picker on order tracking, returns, contact,
       policies, search, accessories and the like. */
    pageAllows().then(function (ok) {
      if (!ok) { track("mp_suppressed", { why: "page" }); return; }
      if (skipped) {
        loadData().then(function (d) {
          if (pageIdentifiesAModel(d)) { track("mp_suppressed_model_page"); return; }
          showPill("Find your iPhone case", function () {
            loadData().then(function (d2) { showPicker(d2); });
          }, null, { pickThenPlay: true });
        }).catch(function () {
          showPill("Find your iPhone case", function () {
            loadData().then(function (d2) { showPicker(d2); });
          }, null, { pickThenPlay: true });
        });
        return;
      }
      setTimeout(function () {
        loadData().then(function (d) {
          if (pageIdentifiesAModel(d)) { track("mp_suppressed_model_page"); return; }
          showPicker(d);
        }).catch(function () {});
      }, CFG.delayPicker);
    });
  }

  /* Exposed so the config can be inspected or flipped from the console on a
     real device — e.g. CKPopup.cfg.gameCoupon = {...} — without a redeploy. */
  window.CKPopup = { cfg: CFG, version: "2.7.1", showPill: showPill, startGame: startGame };

  function boot() {
    if (isIphone) iphoneInit(); else androidInit();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

})();
